import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import {useFluxStore} from 'client/framework';
import sessionStore from 'client/Session/SessionStore';

import type {ManualEvaluation} from '../../shared/FrqEvaluationSummary/FrqEvaluationSummary.types';

import useStudentGuess from './useStudentGuess';

type GuessEvaluation = NonNullable<ReturnType<typeof useStudentGuess>>['teacherEvaluation'];

/** GraphQL nulls → the FE optional-field convention the summary renders from. */
function toManualEvaluation(evaluation: NonNullable<GuessEvaluation>): ManualEvaluation {
  return {
    overallScore: evaluation.overallScore ?? undefined,
    overallFeedback: evaluation.overallFeedback ?? undefined,
    criteriaScores: (evaluation.criteriaScores ?? [])
      .filter((criterion): criterion is NonNullable<typeof criterion> => criterion != null)
      .map((criterion) => ({
        criterionId: criterion.criterionId,
        score: criterion.score ?? undefined,
        feedback: criterion.feedback ?? undefined
      }))
  };
}

/**
 * The student's own released grade for this question, or null while there is nothing they may see.
 *
 * Gated on `gradeAvailable` rather than `evaluationReleased`, because it also folds in the
 * assignment's show-correct-answers setting: a grade released before the due date stays hidden
 * until that setting opens. It is the same condition the field policy on `teacherEvaluation`
 * applies, so a true here means the evaluation actually came back.
 */
export default function useFrqReleasedGrade(
  questionId: string | undefined
): ManualEvaluation | null {
  const isStudent = useFluxStore(sessionStore, (s) => s.isStudent());
  const {isFeatureEnabled} = useIsFeatureFlagEnabled('frq_teacher_grading');
  const guess = useStudentGuess(questionId);

  if (!isFeatureEnabled || !isStudent || guess?.gradeAvailable !== true) {
    return null;
  }

  return guess.teacherEvaluation ? toManualEvaluation(guess.teacherEvaluation) : null;
}
